import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MoreHorizontal from '@/icons/MoreHorizontal';

import './memory-more-menu.css';

export interface MemoryMoreMenuProps {
  onOpen?: () => void;
  onCopyLink?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  showEdit?: boolean;
  showDelete?: boolean;
  triggerClassName?: string;
}

const MemoryMoreMenu: React.FC<MemoryMoreMenuProps> = ({
  onOpen,
  onCopyLink,
  onEdit,
  onDelete,
  showEdit = false,
  showDelete = false,
  triggerClassName = ''
}) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const closeAnd = (fn?: () => void) => {
    setOpen(false);
    fn?.();
  };

  return (
    <div className="memory-more-menu" ref={ref}>
      <button
        type="button"
        className={`memory-more-menu__trigger ${triggerClassName}`.trim()}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((value) => !value);
        }}
        aria-label={t('person.moreActions')}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <MoreHorizontal width={16} height={16} />
      </button>
      {open && (
        <div className="memory-more-menu__panel" role="menu">
          {onOpen && (
            <button type="button" className="memory-more-menu__item" role="menuitem" onClick={() => closeAnd(onOpen)}>
              {t('actions.open')}
            </button>
          )}
          {onCopyLink && (
            <button
              type="button"
              className="memory-more-menu__item"
              role="menuitem"
              onClick={() => closeAnd(onCopyLink)}
            >
              {t('actions.copyLink')}
            </button>
          )}
          {showEdit && onEdit && (
            <button type="button" className="memory-more-menu__item" role="menuitem" onClick={() => closeAnd(onEdit)}>
              {tCommon('edit')}
            </button>
          )}
          {showDelete && onDelete && (
            <>
              <div className="memory-more-menu__divider" aria-hidden />
              <button
                type="button"
                className="memory-more-menu__item memory-more-menu__item--danger"
                role="menuitem"
                onClick={() => closeAnd(onDelete)}
              >
                {tCommon('delete')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MemoryMoreMenu;
